import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Users, MessageSquarePlus, Send, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { PixelOffice, ROOMS } from './PixelOffice';
import type { RoomId } from './PixelOffice';
import { LessonReviewPanel } from './LessonReviewPanel';
import { regenApi } from '../lib/api';

/**
 * The creative team room.
 *
 * Tabs pan the camera across ONE floor plan (they are not separate scenes). From here you can
 * call a meeting, and send feedback aimed at a single persona — which becomes a persona-scoped
 * lesson injected into that persona's own prompt on the next job.
 *
 * Live updates come from SSE; the 8s poll stays as a fallback so the room still works if the
 * stream is blocked by a proxy.
 */

type Decision = { persona: string; rule: string; reason?: string };

export function TeamRoomPanel({ desks, cur, feed, jobId, creativeRef, onSelectPersona, onRefresh, live, autoMeeting }: {
  desks: any[]; cur?: any; feed?: any[]; jobId?: string; creativeRef?: string;
  onSelectPersona?: (id: string) => void; onRefresh?: () => void; live?: boolean;
  autoMeeting?: number;   // changing value = open a meeting for the bound request
}) {
  const [room, setRoom] = useState<RoomId>('all');
  const [meeting, setMeeting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showMeeting, setShowMeeting] = useState(false);

  // ── feedback to one persona ──
  const [fbPersona, setFbPersona] = useState('');
  const [fbNote, setFbNote] = useState('');
  const [fbBusy, setFbBusy] = useState(false);
  const [fbResult, setFbResult] = useState<any>(null);
  const [fbCategory, setFbCategory] = useState('');
  const [proposal, setProposal] = useState<any>(null);   // what we INTEND to teach, pending confirm
  const [proposalRule, setProposalRule] = useState('');
  const [cats, setCats] = useState<{ teaching: any[]; non_teaching: string[] }>({ teaching: [], non_teaching: [] });
  const [showLessons, setShowLessons] = useState(false);

  // Opened from a result card: gather the team and start a meeting about that request.
  useEffect(() => {
    if (!autoMeeting) return;
    setMeeting(true); setRoom('conference'); setShowMeeting(true);
    if (creativeRef) setAgenda(a => a || `Review ${creativeRef}`);
  }, [autoMeeting, creativeRef]);

  useEffect(() => {
    regenApi.teamLessonCategories()
      .then(r => setCats({ teaching: r.data?.teaching || [], non_teaching: r.data?.non_teaching || [] }))
      .catch(() => { /* categories are optional — free text still works */ });
  }, []);

  // ── meeting ──
  const [agenda, setAgenda] = useState('');
  const [notes, setNotes] = useState('');
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [mBusy, setMBusy] = useState(false);
  const [mResult, setMResult] = useState<any>(null);

  const roster = (desks || []).map(d => ({ id: d.id, role: d.role, authority: d.authority }));
  const advisoryOf = (id: string) => roster.find(r => r.id === id)?.authority?.used === false;

  // STEP 1 — interpret. Nothing is saved yet; the person sees who we'll teach and the exact
  // sentence, so a mis-read never becomes a permanent instruction.
  const reviewFeedback = async () => {
    if (!fbNote.trim()) return;
    setFbBusy(true); setFbResult(null); setProposal(null);
    try {
      const res = await regenApi.teamFeedbackPropose({
        note: fbNote.trim(), category: fbCategory || undefined, persona: fbPersona || undefined,
        job_id: jobId || undefined, creative_ref: creativeRef || undefined,
      });
      setProposal(res.data);
      setProposalRule(res.data?.rule || fbNote.trim());
    } catch (e: any) {
      setFbResult({ ok: false, error: e?.response?.data?.error || 'Could not read that feedback' });
    } finally { setFbBusy(false); }
  };

  // STEP 2 — store the wording the person approved.
  const confirmFeedback = async () => {
    if (!proposal?.persona || !proposalRule.trim()) return;
    setFbBusy(true);
    try {
      const res = await regenApi.teamFeedback({
        persona: proposal.persona, note: proposalRule.trim(),
        category: proposal.category || fbCategory || undefined,
        raw_feedback: fbNote.trim(), normalized: !!proposal.normalized,
        context: proposal.context || undefined, confirmed: true,
        job_id: jobId || undefined, creative_ref: creativeRef || undefined,
      });
      setFbResult(res.data);
      if (res.data?.ok) { setFbNote(''); setProposal(null); setFbCategory(''); onRefresh?.(); }
    } catch (e: any) {
      setFbResult({ ok: false, error: e?.response?.data?.error || 'Could not save that feedback' });
    } finally { setFbBusy(false); }
  };

  const submitMeeting = async () => {
    setMBusy(true); setMResult(null);
    try {
      const res = await regenApi.teamMeeting({
        job_id: jobId || undefined, creative_ref: creativeRef || undefined,
        agenda: agenda.trim() || undefined, notes: notes.trim() || undefined,
        attendees: roster.map(r => r.id),
        decisions: decisions.filter(d => d.persona && d.rule.trim()),
      });
      setMResult(res.data);
      if (res.data?.ok) { setDecisions([]); setNotes(''); setAgenda(''); onRefresh?.(); }
    } catch (e: any) {
      setMResult({ ok: false, error: e?.response?.data?.error || 'Could not save the meeting' });
    } finally { setMBusy(false); }
  };

  return (
    <div className="space-y-2">
      {/* ── internal tabs: pan the camera, don't swap the scene ── */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {ROOMS.map(r => (
          <button key={r.id} onClick={() => { setRoom(r.id as RoomId); if (r.id !== 'conference') setMeeting(false); }}
            className={`text-xs px-3 py-1.5 rounded-lg border transition ${
              room === r.id ? 'bg-primary text-white border-primary' : 'border-border text-gray-400 hover:bg-white/5'}`}>
            {r.name}
          </button>
        ))}
        <span title={live ? 'Live event stream connected' : 'Stream unavailable — falling back to polling'}
          className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full ${
            live ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-500/15 text-slate-400'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'}`} />
          {live ? 'live' : 'polling'}
        </span>
        <div className="flex-1" />
        <button
          onClick={() => { const on = !meeting; setMeeting(on); if (on) { setRoom('conference'); setShowMeeting(true); } }}
          className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition ${
            meeting ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-500/50 text-amber-300 hover:bg-amber-500/10'}`}>
          <Users className="w-3.5 h-3.5" />{meeting ? 'In meeting' : 'Call a meeting'}
        </button>
        <button onClick={() => setShowFeedback(v => !v)}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-border text-gray-300 hover:bg-white/5">
          <MessageSquarePlus className="w-3.5 h-3.5" />Give feedback
        </button>
      </div>

      <PixelOffice desks={desks} cur={cur} feed={feed} room={room} meeting={meeting}
        onSelectPersona={onSelectPersona} />

      {/* ── feedback to a single persona ── */}
      {showFeedback && (
        <div className="rounded-2xl border border-border p-3 space-y-2 bg-black/20">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-200">Feedback to one role</h4>
            <button onClick={() => setShowFeedback(false)} className="text-gray-500 hover:text-gray-300"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-[11px] text-gray-400">
            Goes only to the role you pick — it's injected into that role's own prompt on the next job.
            {creativeRef ? <> Attached to <span className="text-gray-300">{creativeRef}</span>.</> : null}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {roster.map(r => (
              <button key={r.id} onClick={() => setFbPersona(r.id)}
                className={`text-[11px] px-2 py-1 rounded-lg border transition ${
                  fbPersona === r.id ? 'bg-primary text-white border-primary' : 'border-border text-gray-400 hover:bg-white/5'
                } ${advisoryOf(r.id) ? 'opacity-60' : ''}`}>
                {r.role}{advisoryOf(r.id) ? ' · advisory' : ''}
              </button>
            ))}
          </div>
          {fbPersona && advisoryOf(fbPersona) && (
            <div className="flex items-start gap-1.5 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
              <span>This lane discards this role's output, so the note will be saved and learned but
                <strong> will not change the rendered video</strong>. Aim it at a role that runs this lane if you want output to change.</span>
            </div>
          )}
          {/* What KIND of problem — this is the attribution, so no keyword guessing is needed */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] text-gray-500 mr-1">What's wrong?</span>
            {cats.teaching.map((c: any) => (
              <button key={c.category} onClick={() => { setFbCategory(c.category); setFbPersona(c.persona); setProposal(null); }}
                title={`Teaches the ${c.role}`}
                className={`text-[11px] px-2 py-1 rounded-lg border transition ${
                  fbCategory === c.category ? 'bg-primary text-white border-primary' : 'border-border text-gray-400 hover:bg-white/5'}`}>
                {c.category}
              </button>
            ))}
            {cats.non_teaching.map((c: string) => (
              <button key={c} onClick={() => { setFbCategory(c); setFbPersona(''); setProposal(null); }}
                title="Recorded for people — does not train the team"
                className={`text-[11px] px-2 py-1 rounded-lg border transition ${
                  fbCategory === c ? 'bg-slate-600 text-white border-slate-500' : 'border-border text-gray-500 hover:bg-white/5'}`}>
                {c}
              </button>
            ))}
          </div>

          <textarea value={fbNote} onChange={e => { setFbNote(e.target.value); setProposal(null); }} rows={2}
            placeholder='e.g. "Stop writing stage directions into the spoken script."'
            className="w-full text-xs px-2.5 py-2 rounded-lg bg-black/30 border border-border text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-primary/50" />

          {/* ── Confirmation: nothing is learned until this is approved ── */}
          {proposal && proposal.teaches === false && (
            <div className="rounded-lg border border-slate-500/30 bg-slate-500/10 p-2 text-[11px] text-slate-300">
              {proposal.reason_not_teaching}
            </div>
          )}
          {proposal && proposal.teaches && (
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-2.5 space-y-2">
              <p className="text-[11px] text-gray-300">
                We'll teach the <strong className="text-white">{proposal.role}</strong>
                {proposal.inferred_persona && <span className="text-amber-300"> (we guessed this role — change the category if it's wrong)</span>}:
              </p>
              <input value={proposalRule} onChange={e => setProposalRule(e.target.value)}
                className="w-full text-xs px-2.5 py-2 rounded-lg bg-black/40 border border-border text-gray-100" />
              {proposal.normalized && (
                <p className="text-[10px] text-gray-500">Reworded so it still makes sense on a future job. Edit freely.</p>
              )}
              {proposal.needs_wording && (
                <p className="text-[10px] text-amber-300">We couldn't turn this into a reusable rule — please word it yourself.</p>
              )}
              <div className="flex items-center gap-2">
                <button disabled={fbBusy || !proposalRule.trim()} onClick={confirmFeedback}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary text-white disabled:opacity-50">
                  {fbBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Correct — teach this
                </button>
                <button onClick={() => setProposal(null)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-border text-gray-300 hover:bg-white/5">
                  Not right
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            {!proposal && (
              <button disabled={fbBusy || !fbNote.trim()} onClick={reviewFeedback}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary text-white disabled:opacity-50">
                {fbBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Review before teaching
              </button>
            )}
            <button onClick={() => setShowLessons(v => !v)}
              className="text-xs px-3 py-1.5 rounded-lg border border-border text-gray-300 hover:bg-white/5">
              {showLessons ? 'Hide' : 'See'} what the team learned
            </button>
            {fbResult?.ok && (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {fbResult.lesson_written === false ? (fbResult.note || 'Recorded') : 'Saved — applies on the next job'}
                {fbResult.advisory ? ' (advisory only)' : ''}
              </span>
            )}
            {fbResult && !fbResult.ok && <span className="text-[11px] text-red-400">{fbResult.error}</span>}
          </div>

          {showLessons && <LessonReviewPanel personaFilter={fbPersona || undefined} />}
        </div>
      )}

      {/* ── meeting: free-text notes + structured decisions ── */}
      {showMeeting && (
        <div className="rounded-2xl border border-amber-500/30 p-3 space-y-2 bg-amber-500/5">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-amber-200">Meeting</h4>
            <button onClick={() => setShowMeeting(false)} className="text-gray-500 hover:text-gray-300"><X className="w-4 h-4" /></button>
          </div>
          <input value={agenda} onChange={e => setAgenda(e.target.value)} placeholder="Agenda — what is this about?"
            className="w-full text-xs px-2.5 py-2 rounded-lg bg-black/30 border border-border text-gray-100 placeholder:text-gray-500" />
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="Notes (kept for people — never parsed, so nothing is misread as an instruction)"
            className="w-full text-xs px-2.5 py-2 rounded-lg bg-black/30 border border-border text-gray-100 placeholder:text-gray-500" />

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-gray-300">Decisions — these are what the team learns</span>
              <button onClick={() => setDecisions(d => [...d, { persona: '', rule: '' }])}
                className="text-[11px] px-2 py-1 rounded-lg border border-border text-gray-300 hover:bg-white/5">+ Add</button>
            </div>
            {decisions.length === 0 && <p className="text-[11px] text-gray-500">No decisions yet — notes alone teach the team nothing.</p>}
            {decisions.map((d, i) => (
              <div key={i} className="flex gap-1.5 items-start">
                <select value={d.persona}
                  onChange={e => setDecisions(list => list.map((x, j) => j === i ? { ...x, persona: e.target.value } : x))}
                  className="text-[11px] px-2 py-1.5 rounded-lg bg-black/30 border border-border text-gray-200 shrink-0">
                  <option value="">role…</option>
                  {roster.map(r => <option key={r.id} value={r.id}>{r.role}</option>)}
                </select>
                <input value={d.rule} placeholder="the rule they must follow next time"
                  onChange={e => setDecisions(list => list.map((x, j) => j === i ? { ...x, rule: e.target.value } : x))}
                  className="flex-1 min-w-0 text-[11px] px-2 py-1.5 rounded-lg bg-black/30 border border-border text-gray-100 placeholder:text-gray-500" />
                <button onClick={() => setDecisions(list => list.filter((_, j) => j !== i))}
                  className="text-gray-500 hover:text-red-400 p-1"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button disabled={mBusy} onClick={submitMeeting}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-amber-500 text-white disabled:opacity-50">
              {mBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Save meeting
            </button>
            <button onClick={() => setMeeting(false)} className="text-xs px-3 py-1.5 rounded-lg border border-border text-gray-300 hover:bg-white/5">
              End meeting
            </button>
            {mResult?.ok && <span className="text-[11px] text-emerald-400">Saved — {mResult.lessons_written} lesson(s) written</span>}
            {mResult && !mResult.ok && <span className="text-[11px] text-red-400">{mResult.error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamRoomPanel;
